import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import LanguageSwitcher from '../../translation/languageSwitcher';

export default function Header() {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { getCartCount } = useCart();
  const { isAuthenticated, user, logout } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [mobileMenuOpen]);

  const isActive = (path) => {
    if (path === '/shop') {
      return location.pathname === '/shop' || location.pathname.startsWith('/product');
    }
    return location.pathname === path;
  };

  const handleLogout = () => {
    logout();
    navigate('/shop');
    setMobileMenuOpen(false);
  };

  const isMobile = windowWidth < 768;

  const categories = [
    { path: '/', label: t('header.home') },
    { path: '/shop', label: t('header.products') },
    { path: '/my-orders', label: t('header.orders') },
    { path: '/contact', label: t('header.contact') },
  ];

  const styles = {
    headerContainer: {
      position: 'fixed',
      zIndex: 1000,
      width: '100%',
      height: '80px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    header: {
      position: 'relative',
      top: 0,
      width: '95%',
      height: '50px',
      backgroundColor: '#fff',
      borderBottom: '1px solid #e5e5e5',
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'spaceBetween',
      zIndex: 1000,
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    },
    desktopHeader: {
      display: isMobile ? 'none' : 'grid',
      gridTemplateColumns: '1fr auto 1fr',
      alignItems: 'center',
      padding: '20px 40px',
      maxWidth: '1400px',
      margin: '0 auto',
    },
    categories: {
      display: 'flex',
      gap: '30px',
      listStyle: 'none',
      margin: 0,
      padding: 0,
    },
    categoryLink: {
      fontSize: '13px',
      fontWeight: '600',
      color: '#000',
      textDecoration: 'none',
      cursor: 'pointer',
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
      transition: 'opacity 0.2s',
        whiteSpace: 'nowrap', // ✅ prevents line break
    },
    logo: {
      fontSize: '28px',
      fontWeight: '700',
      color: '#000',
      letterSpacing: '2px',
      cursor: 'pointer',
      textTransform: 'uppercase',
      padding: '0 90px',
      position: 'relative',
      right: '35px',
    },
    rightIcons: {
      display: 'flex',
      gap: '20px',
      alignItems: 'center',
      justifyContent: 'flex-end',
    },
    iconButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontSize: '20px',
      padding: '8px',
      color: '#000',
      position: 'relative',
      transition: 'opacity 0.2s',
    },
    mobileHeader: {
      display: isMobile ? 'grid' : 'none',
      gridTemplateColumns: '1fr auto 1fr',
      alignItems: 'center',
      padding: '15px 20px',
    },
    mobileLogo: {
      fontSize: '20px',
      fontWeight: '700',
      color: '#000',
      letterSpacing: '1px',
      gridColumn: '2',
      textAlign: 'center',
      textTransform: 'uppercase',
      marginRight: '25px', 
    },
    mobileRightIcons: {
      display: 'flex',
      gap: '12px',
      alignItems: 'center',
      justifyContent: 'flex-end',
    },
    mobileIconButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontSize: '18px',
      padding: '6px',
      color: '#000',
      position: 'relative',
    },
    hamburger: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '6px',
    },
    hamburgerLine: {
      width: '20px',
      height: '2px',
      backgroundColor: '#000',
    },
    cartBadge: {
      position: 'absolute',
      top: '2px',
      right: '2px',
      backgroundColor: '#000',
      color: '#fff',
      borderRadius: '50%',
      width: '16px',
      height: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '10px',
      fontWeight: '600',
    },
    overlay: {
      display: mobileMenuOpen ? 'block' : 'none',
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1001,
    },
    mobileMenu: {
      position: 'fixed',
      top: 0,
      right: mobileMenuOpen ? 0 : '-100%',
      width: '300px',
      height: '100%',
      backgroundColor: '#fff',
      boxShadow: '-2px 0 10px rgba(0, 0, 0, 0.1)',
      transition: 'right 0.3s ease',
      zIndex: 1002,
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
    },
    mobileMenuHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '20px',
      borderBottom: '1px solid #e5e5e5',
    },
    closeBtn: {
      background: 'none',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: '#000',
      padding: '5px',
    },
    mobileCategories: {
      display: 'flex',
      flexDirection: 'column',
      padding: '20px',
      borderBottom: '1px solid #e5e5e5',
    },
    mobileCategoryLink: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#000',
      textDecoration: 'none',
      cursor: 'pointer',
      padding: '12px 0',
      borderBottom: '1px solid #f5f5f5',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      background: 'none',
      border: 'none',
      textAlign: 'left',
    },
    mobileLanguageSection: {
      padding: '20px',
      borderBottom: '1px solid #e5e5e5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    mobileAuthSection: {
      padding: '20px',
      marginTop: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    mobileUserInfo: {
      padding: '12px',
      backgroundColor: '#f5f5f5',
      borderRadius: '4px',
      textAlign: 'center',
      fontSize: '14px',
      fontWeight: '500',
    },
    mobileAuthButton: {
      padding: '12px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      borderRadius: '4px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    mobileLoginBtn: {
      backgroundColor: 'transparent',
      border: '2px solid #000',
      color: '#000',
    },
    mobileLogoutBtn: {
      backgroundColor: 'transparent',
      border: '1px solid #e5e5e5',
      color: '#666',
    },
  };

  return (
    <>
      <div style={styles.headerContainer}>
        <header style={styles.header}>
          {/* Desktop Header */}
          <div style={styles.desktopHeader}>
            {/* Categories - Left */}
            <nav>
              <ul style={styles.categories}>
                {categories.map((cat) => (
                  <li key={cat.path}>
                    <a 
                      style={styles.categoryLink}
                      onClick={() => navigate(cat.path)}
                      onMouseOver={(e) => e.target.style.opacity = '0.6'}
                      onMouseOut={(e) => e.target.style.opacity = '1'}
                    >
                      {cat.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Logo - Center */}
            <div 
              style={styles.logo}
              onClick={() => navigate('/')}
            >
              BEAUTYPRO
            </div>

            {/* Right Icons */}
            <div style={styles.rightIcons}>
              <LanguageSwitcher />

              <button 
                style={styles.iconButton}
                onClick={() => navigate('/shop')}
                onMouseOver={(e) => e.target.style.opacity = '0.6'}
                onMouseOut={(e) => e.target.style.opacity = '1'}
                title={t('header.search')}
              >
                <img style={{width: '16px'}} src={require('../../assets/search.png')} alt="Search" />
              </button>

              <button 
                style={styles.iconButton}
                onClick={() => navigate('/favorites')}
                onMouseOver={(e) => e.target.style.opacity = '0.6'}
                onMouseOut={(e) => e.target.style.opacity = '1'}
                title={t('header.favorites')}
              >
                <img style={{width: '16px'}} src={require('../../assets/favourites.png')} alt="Favorites" />
              </button>

              {isAuthenticated ? (
                <button 
                  style={styles.iconButton}
                  onClick={handleLogout}
                  onMouseOver={(e) => e.target.style.opacity = '0.6'}
                  onMouseOut={(e) => e.target.style.opacity = '1'}
                  title={t('header.logout')}
                >
                    <img style={{width: '16px'}} src={require('../../assets/logout.png')} alt="Logout" />
                </button>
              ) : (
                <button 
                  style={styles.iconButton}
                  onClick={() => navigate('/login')}
                  onMouseOver={(e) => e.target.style.opacity = '0.6'}
                  onMouseOut={(e) => e.target.style.opacity = '1'}
                  title={t('header.login')}
                >
                  <img style={{width: '16px'}} src={require('../../assets/login_icon.png')} alt="Login" />
                </button>
              )}

              <button 
                style={styles.iconButton}
                onClick={() => navigate('/cart')}
                onMouseOver={(e) => e.target.style.opacity = '0.6'}
                onMouseOut={(e) => e.target.style.opacity = '1'}
                title={t('header.cart')}
              >
                <img style={{width: '16px'}} src={require('../../assets/cart.png')} alt="Cart" />
                {getCartCount() > 0 && (
                  <span style={styles.cartBadge}>{getCartCount()}</span>
                )}
              </button>
            </div>
          </div>

          {/* Mobile Header */}
          <div style={styles.mobileHeader}>
            <div></div>
            
            <div 
              style={styles.mobileLogo}
              onClick={() => navigate('/')}
            >
              BEAUTYPRO
            </div>

            <div style={styles.mobileRightIcons}>
              <button 
                style={styles.mobileIconButton}
                onClick={() => navigate('/shop')}
              >
                <img style={{width: '16px'}} src={require('../../assets/search.png')} alt="Search" />
              </button>

              <button 
                style={styles.mobileIconButton}
                onClick={() => navigate('/favorites')}
              >
                <img style={{width: '16px'}} src={require('../../assets/favourites.png')} alt="Favorites" />
              </button>

              <button 
                style={styles.mobileIconButton}
                onClick={() => navigate('/cart')}
              >
                <img style={{width: '16px'}} src={require('../../assets/cart.png')} alt="Cart" />
                {getCartCount() > 0 && (
                  <span style={styles.cartBadge}>{getCartCount()}</span>
                )}
              </button>

              <button 
                style={styles.hamburger}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <span style={styles.hamburgerLine}></span>
                <span style={styles.hamburgerLine}></span>
                <span style={styles.hamburgerLine}></span>
              </button>
            </div>
          </div>
        </header>
      </div>

      {/* Mobile Menu Overlay */}
      <div 
        style={styles.overlay}
        onClick={() => setMobileMenuOpen(false)}
      ></div>

      {/* Mobile Sidebar Menu */}
      <div style={styles.mobileMenu}>
        <div style={styles.mobileMenuHeader}>
          <div style={{ fontSize: '18px', fontWeight: '700', letterSpacing: '1px' }}>
            BEAUTYPRO
          </div>
          <button 
            style={styles.closeBtn}
            onClick={() => setMobileMenuOpen(false)}
          >
            ✕
          </button>
        </div>

        {/* Navigation Categories */}
        <div style={styles.mobileCategories}>
          {categories.map((cat) => (
            <button
              key={cat.path}
              style={styles.mobileCategoryLink}
              onClick={() => {
                navigate(cat.path);
                setMobileMenuOpen(false);
              }}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Language Switcher in Mobile Menu */}
        <div style={styles.mobileLanguageSection}>
          <span style={{ 
            fontSize: '14px', 
            fontWeight: '600',
            color: '#666',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {t('header.language')}
          </span>
          <LanguageSwitcher isMobile={true} />
        </div>

        {/* Mobile Auth */}
        <div style={styles.mobileAuthSection}>
          {isAuthenticated ? (
            <>
              <div style={styles.mobileUserInfo}>
                👤 {user?.name}
              </div>
              <button 
                style={{...styles.mobileAuthButton, ...styles.mobileLogoutBtn}}
                onClick={handleLogout}
              >
                {t('header.logout')}
              </button>
            </>
          ) : (
            <button 
              style={{...styles.mobileAuthButton, ...styles.mobileLoginBtn}}
              onClick={() => {
                navigate('/login');
                setMobileMenuOpen(false);
              }}
            >
              {t('header.login')}
            </button>
          )}
        </div>
      </div>
    </>
  );
}